import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, GraduationCap, HeartHandshake, Stethoscope } from "lucide-react";
import Seo from "@/components/np/Seo";
import SectionHeading from "@/components/np/SectionHeading";
import BookButton from "@/components/np/BookButton";
import RatingBadge from "@/components/np/RatingBadge";
import FinalCta from "@/components/home/FinalCta";
import { doctor, memberships, meta } from "@/content";

const PILLARS = [
  { icon: Stethoscope, title: "Legitimerad läkare", text: doctor.title + "." },
  { icon: GraduationCap, title: "Utbildning genom AAOM", text: doctor.training },
  { icon: HeartHandshake, title: "Volontärarbete", text: doctor.volunteer },
];

export default function Om() {
  return (
    <>
      <Seo {...meta.om} />
      <section className="page-hero" aria-labelledby="om-title">
        <div className="container-np grid items-center gap-12 py-16 sm:py-20 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Om Nordic Prolo</p>
            <h1 id="om-title" className="mt-4 font-serif text-5xl leading-[1.02] text-ink sm:text-6xl lg:text-7xl">En läkare som tar sig tid att förstå din smärta</h1>
            {doctor.bio.map((p) => <p key={p.slice(0, 20)} className="mt-6 max-w-2xl text-lg leading-relaxed text-muted">{p}</p>)}
            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <BookButton testId="om-book-button" size="lg" />
              <RatingBadge />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="arch mx-auto max-w-[420px] overflow-hidden bg-pine">
              <img src={doctor.photo} alt={doctor.photoAlt} width="1024" height="910" className="h-full w-full object-cover object-[50%_30%]" />
            </div>
            <p className="mt-5 text-center font-serif text-2xl text-ink">{doctor.name}</p>
            <p className="text-center text-sm text-muted">{doctor.title}</p>
          </div>
        </div>
      </section>
      <section className="py-16 sm:py-20 lg:py-24" aria-labelledby="cred-title">
        <div className="container-np">
          <SectionHeading id="cred-title" eyebrow="Bakgrund" title="Bakgrund och medlemskap" />
          <ul className="mt-12 grid gap-5 md:grid-cols-3">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <li key={title} className="rounded-[28px] bg-white p-7 ring-1 ring-line">
                <Icon className="h-7 w-7 text-pine" aria-hidden="true" />
                <h3 className="mt-5 font-serif text-2xl text-ink">{title}</h3>
                <p className="mt-3 leading-relaxed text-muted">{text}</p>
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-[28px] bg-forest p-7 text-cream sm:p-10">
            <h3 className="font-serif text-2xl">{doctor.name} är medlem i</h3>
            <ul className="mt-5 grid gap-3 sm:grid-cols-3">
              {memberships.map((m) => (
                <li key={m.name}>
                  <a href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`membership-link-${m.name.split(" ")[0].toLowerCase()}`} className="flex items-center justify-between gap-3 rounded-2xl bg-cream/10 px-5 py-4 transition-colors hover:bg-cream/15">
                    {m.name} <ArrowUpRight className="h-4 w-4 shrink-0 text-amber" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <section className="bg-sand/60 py-16 sm:py-20 lg:py-24" aria-labelledby="how-title">
        <div className="container-np grid gap-10 lg:grid-cols-12">
          <SectionHeading id="how-title" className="lg:col-span-5" eyebrow="Så arbetar vi" title="Individuella planer, inte standardlösningar" />
          <div className="space-y-5 text-lg leading-relaxed text-muted lg:col-span-7">
            <p>Behandlingsplanen utgår från läkarbedömningen och dina besvär. Beroende på vad som är lämpligt kan den omfatta proloterapi, nervproloterapi, PRP eller MLS-laser. Vid nervsymtom kan även vidare utredning vara aktuell, till exempel vid misstänkt B12-brist.</p>
            <p>Nordic Prolo har mottagning i Nacka Forum. Dr Hivar tar även emot i Göteborg en gång per månad.</p>
            <div className="flex flex-wrap gap-6 pt-2 text-base">
              <Link to="/behandlingar" className="link-arrow" data-testid="om-treatments-link">Behandlingar <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/kliniker-boka" className="link-arrow" data-testid="om-clinics-link">Kliniker & boka <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>
      <div className="pt-16 sm:pt-20"><FinalCta /></div>
    </>
  );
}
